import '@assets/styles/components/_aid-modal.scss';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { TextInput } from '@components/common';
import { IRigidStatusModalProps } from '.';
import { useRigidStatusMutation } from '@services/hooks/endusers/rigidStatusQuery';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { ToastType, useTosterStore } from '@store/toster';
import { useValidation } from '@hooks/validations';

const initialValues = {
	reason: '',
};

export const RigidStatusModal = ({
	closeModal,
	action,
	contractorId,
	contractorSfid,
	refetchRigidUsers,
}: IRigidStatusModalProps): ReactElement => {
	const { mutate, isPending } = useRigidStatusMutation();
	const { setToaster } = useTosterStore();
	const { getRigidStatusModalValidationSchema } = useValidation();
	const isApprove = action === 'Approved';
	const actionLabel = isApprove ? 'Approval' : 'Rejection';

	const formik = useFormik({
		initialValues,
		validationSchema: getRigidStatusModalValidationSchema(actionLabel),
		onSubmit: () => {
			handleSubmitForm();
		},
	});

	useEffect(() => {
		return (): void => {
			formik.resetForm();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isSubmitDisabled = useMemo(
		() => !contractorId || isPending || !formik.values.reason?.trim(),
		[contractorId, formik.values.reason, isPending]
	);

	const handleSubmitForm = useCallback(() => {
		if (!contractorId) {
			return;
		}

		const reason = formik.values.reason.trim();

		mutate(
			{
				id: contractorId,
				venuestatus_c: action,
				reason_for_approval_rejection_c: reason,
			},
			{
				onSuccess: () => {
					const updateLocalStatus = async (): Promise<void> => {
						if (!contractorSfid) {
							return;
						}
						const db = await getDatabase();
						const doc = await db.all_rigid.findOne({ selector: { sfid: contractorSfid } }).exec();
						if (doc) {
							await doc.patch({ venuestatus_c: action });
						}
					};

					void updateLocalStatus();
					if (refetchRigidUsers) {
						refetchRigidUsers();
					}
					formik.resetForm();
					setToaster(true, ToastType.success, `Contractor ${action.toLowerCase()} successfully`);
					closeModal();
				},
				onError: (error: Error) => {
					setToaster(true, ToastType.error, error.message);
				},
			}
		);
	}, [action, closeModal, contractorId, contractorSfid, formik, mutate, refetchRigidUsers, setToaster]);

	return (
		<div className="aid-modal-wrapper">
			<form onSubmit={formik.handleSubmit}>
				<div className="row">
					<div className="form-control d-col d-col-12">
						<TextInput
							id="reason"
							name="reason"
							type="text"
							onBlur={formik.handleBlur}
							value={formik.values.reason ?? ''}
							setFieldValue={formik.setFieldValue}
							label={`Reason for ${actionLabel}`}
							error={formik.touched.reason && formik.errors.reason ? formik.errors.reason : null}
							required
						/>
					</div>
				</div>

				<div className="btn-wrapper">
					<button type="submit" aria-label="submit button" className="btn-primary btn" disabled={isSubmitDisabled}>
						{isPending ? 'Submitting...' : 'Submit'}
					</button>
				</div>
			</form>
		</div>
	);
};
